import { Box } from '@hopskipdrive/react-components';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import MuiButton from '@mui/material/Button';
import MuiTooltip from '@mui/material/Tooltip';

import type { Recording } from '../../../api/services/Recordings';
import { RiderServices } from '../../../riders/features/RiderServices';
import { colorPalette } from '../../../shared/components/variables';

type FormatInTimezone = (date?: Date | string, format?: string) => string;

const DateCell = ({ startedAt, formatInTimezone }: { startedAt: string; formatInTimezone: FormatInTimezone }) => (
  <Box>
    <div>{formatInTimezone(startedAt, 'EEE,')}</div>
    <div>{formatInTimezone(startedAt, 'MM/dd/yyyy')}</div>
  </Box>
);

const RidersCell = ({ riderNames }: { riderNames: string[] }) => (
  <Box>
    <div>{riderNames.length}</div>
    <div style={{ color: colorPalette.neutralVariant50 }}>{riderNames.join(', ')}</div>
  </Box>
);

interface ActionCellProps {
  recording: Recording;
  onDownload: (recording: Recording) => void;
}

const ACTION_BTN_BASE_SX = {
  height: '32px',
  minWidth: '90px',
  borderRadius: '20px',
  fontSize: '14px',
  fontWeight: 400,
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': { boxShadow: 'none' },
  '&:active': { boxShadow: 'none' },
} as const;

const ActionCell = ({ recording, onDownload }: ActionCellProps) => {
  if (recording.recordingStatus === 'unavailable') {
    return (
      <MuiTooltip
        title="Recordings become available within 24 hours of the ride. If a recording is not available after longer than 24 hours, this may be due to technical issues during recording or upload. HopSkipDrive is alerted when this occurs and we are constantly working to improve recording quality and reliability."
        placement="top"
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: '#393939',
              borderRadius: '5px',
              px: '24px',
              py: '12px',
              fontSize: '13px',
              fontWeight: 500,
              maxWidth: '340px',
              lineHeight: 'normal',
            },
          },
        }}
      >
        <span>
          <MuiButton
            variant="contained"
            disabled
            sx={{
              ...ACTION_BTN_BASE_SX,
              '&.Mui-disabled': {
                backgroundColor: colorPalette.neutral95,
                color: colorPalette.neutralVariant50,
              },
            }}
          >
            Unavailable
          </MuiButton>
        </span>
      </MuiTooltip>
    );
  }

  return (
    <MuiButton
      variant="contained"
      startIcon={<DownloadRoundedIcon sx={{ fontSize: '16px !important' }} />}
      sx={{
        ...ACTION_BTN_BASE_SX,
        backgroundColor: colorPalette.navy99,
        color: colorPalette.navy30,
        '&:hover': { ...ACTION_BTN_BASE_SX['&:hover'], backgroundColor: colorPalette.navy95 },
      }}
      onClick={(e) => {
        e.stopPropagation();
        onDownload(recording);
      }}
    >
      Download
    </MuiButton>
  );
};

export const getRecordingsColumns = (
  onDownload: (recording: Recording) => void,
  formatInTimezone: FormatInTimezone
) => [
  {
    label: 'Date',
    align: 'left' as const,
    valueGetter: (row: Recording) => <DateCell startedAt={row.startedAt} formatInTimezone={formatInTimezone} />,
  },
  {
    label: 'Riders',
    align: 'left' as const,
    valueGetter: (row: Recording) => <RidersCell riderNames={row.riderNames} />,
  },
  {
    label: 'Planned Pickup Time',
    align: 'left' as const,
    valueGetter: (row: Recording) => formatInTimezone(row.startedAt, 'h:mm aa'),
  },
  {
    label: 'Pickup Location',
    align: 'left' as const,
    valueGetter: (row: Recording) => row.pickupLocationName,
  },
  {
    label: 'Stops',
    align: 'left' as const,
    valueGetter: (row: Recording) => String(row.stops),
  },
  {
    label: 'Drop-Off Location',
    align: 'left' as const,
    valueGetter: (row: Recording) => row.dropoffLocationName,
  },
  {
    label: 'Tags',
    align: 'left' as const,
    valueGetter: (row: Recording) => row.tags.join(', '),
  },
  {
    label: 'CareDriver',
    align: 'left' as const,
    valueGetter: (row: Recording) => row.driverName,
  },
  {
    label: 'Services',
    align: 'left' as const,
    valueGetter: (row: Recording) => <RiderServices services={row.services} />,
  },
  {
    label: 'Actions',
    align: 'right' as const,
    minWidth: '154px',
    maxWidth: '154px',
    disableCellClick: true,
    valueGetter: (row: Recording) => <ActionCell recording={row} onDownload={onDownload} />,
  },
];
